/* eslint-disable react/display-name */
import React, { Suspense, lazy, useCallback, useMemo } from 'react';
import {
  Switch,
  Route,
  Redirect,
  useHistory,
  useRouteMatch,
} from 'react-router-dom';
import { AppShell } from '@tapresearch/ui/components';
import { useMediaQuery } from '@tapresearch/ui/hooks';

import { LinkedInConversionTracker } from 'Shared/Tracking/LinkedInConversionTracker';
import {
  AccountMenu,
  CurrentUserLabel,
  useSidebarNav,
  useInactivePublisher,
  OrganizationalContextBanner,
} from 'Shared/SiteNav';
import { HeaderContent } from './HeaderContent';

const Help = lazy(() =>
  import(/* webpackChunkName: 'help' */ '../components/help')
);
const Projects = lazy(() =>
  import(/* webpackChunkName: 'projects' */ '../components/project/Projects')
);
const Project = lazy(() =>
  import(/* webpackChunkName: 'project' */ '../components/project/index')
);
const Campaign = lazy(() =>
  import(/* webpackChunkName: 'campaign' */ '../components/campaign/index')
);
const Tracker = lazy(() =>
  import(/* webpackChunkName: 'tracker' */ '../components/tracker')
);

import Startup from './Startup';

export default function InsightsPlatformLoader({ ...props }) {
  return (
    <Startup {...props}>
      <LinkedInConversionTracker />
      <Suspense fallback={<div />}>
        <AppShellContainer user={props.currentUser} assets={props.assets} />
      </Suspense>
    </Startup>
  );
}

const AppShellContainer = ({ user, assets }) => {
  const history = useHistory();
  const [isSmall] = useMediaQuery('(max-width: 1200px)');
  const { role } = (user && user.organization_membership) || {};

  const isInactivePublisher = useInactivePublisher(user);

  const onNavigate = useCallback(
    (path, internal = false) => {
      if (internal) return history.push(path);
      window.location.href = path;
    },
    [history]
  );

  const isLinkActive = {
    surveys: true,
  };

  const isHomepage = useRouteMatch({
    path: ['/'],
    exact: true,
  });

  const sideNavItems = useSidebarNav({
    user,
    onNavigate,
    isLinkActive,
    internal: 'insights_platform',
    constants: assets.constants,
    isInactivePublisher,
  });

  const sidebarTopSection = useMemo(
    () =>
      user &&
      user.is_ops &&
      role === assets.constants.ORGANIZATION_ROLE_CUSTOMER_SUCCESS && (
        <OrganizationalContextBanner
          organizationName={user.organization.company_name}
        />
      ),
    [assets, role, user]
  );

  return (
    <AppShell
      padding="xs"
      userMenuLabel={
        <CurrentUserLabel user={user} constants={assets.constants} />
      }
      userMenuContent={<AccountMenu user={user} asset={assets} />}
      sideNavItems={sideNavItems}
      sidebarTopSection={sidebarTopSection}
      headerContent={<HeaderContent isHomepage={isHomepage} />}
      noSidebarToggle={!isHomepage}
      isFullscreen={!isHomepage}
      innerShellProps={{
        ...(!isHomepage && {
          maxWidth: '1300px',
          margin: '0 auto',
        }),
      }}
      headerProps={{
        sx: {
          position: isSmall ? 'sticky' : 'fixed',
          marginLeft:
            isSmall && !!isHomepage ? 'var(--mantine-navbar-width,0)' : 0,
        },
      }}
      sx={{
        minWidth: '1200px',
        main: {
          ...(isSmall && {
            paddingTop: '1rem',
          }),
        },
      }}
    >
      <Switch>
        <Redirect
          exact
          from="/dashboard/projects/:projectIdentifier/campaigns/:campaignIdentifier"
          to="/projects/:projectIdentifier/campaigns/:campaignIdentifier"
        />
        <Redirect
          exact
          from="/dashboard/projects/:projectIdentifier/dashboard"
          to="/projects/:projectIdentifier/dashboard"
        />
        <Redirect
          exact
          from="*/projects/:projectIdentifier"
          to="/projects/:projectIdentifier/dashboard"
        />
        <Route path={`*/tracker`} component={Tracker} />
        <Route path={`*/help`} component={Help} />
        <Route
          path={`*/projects/:projectIdentifier/dashboard`}
          component={Project}
        />
        <Route
          path={`*/projects/:projectIdentifier/campaigns/:campaignIdentifier`}
          component={Campaign}
        />
        <Redirect exact from="/dashboard" to="/" />
        <Route path="/" component={Projects} />
      </Switch>
    </AppShell>
  );
};
